// src/hooks/useActiveMode.js
// Permite que un user con role='provider' use la app temporalmente en
// "modo cliente" para publicar solicitudes propias, sin modificar su rol
// en DB. Se guarda en localStorage.
//
// - Si role='client' o 'admin': activeMode siempre = role
// - Si role='provider': activeMode puede ser 'provider' (default) o 'client'
//   según lo que tenga en localStorage
import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'bellason_active_mode'
const EVENT_NAME  = 'bellason:active-mode-changed'

function readMode(role) {
  if (role !== 'provider') return role
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'client' ? 'client' : 'provider'
  } catch {
    return 'provider'
  }
}

export function useActiveMode(role) {
  const [activeMode, setActiveModeState] = useState(() => readMode(role))

  // Re-leer cuando cambia role
  useEffect(() => {
    setActiveModeState(readMode(role))
  }, [role])

  // Escuchar cambios desde OTRAS instancias del hook (otra pantalla cambia el
  // modo y este componente debe enterarse). Sin esto, App.jsx no se entera de
  // que Profile cambió a 'client' y el routing no responde.
  useEffect(() => {
    function onModeChange() {
      setActiveModeState(readMode(role))
    }
    function onStorage(e) {
      if (e.key === STORAGE_KEY) onModeChange()
    }
    window.addEventListener(EVENT_NAME, onModeChange)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(EVENT_NAME, onModeChange)
      window.removeEventListener('storage', onStorage)
    }
  }, [role])

  const setActiveMode = useCallback((mode) => {
    if (role !== 'provider') return
    const next = mode === 'client' ? 'client' : 'provider'
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ok */ }
    setActiveModeState(next)
    // Notifica a todas las demás instancias del hook
    window.dispatchEvent(new CustomEvent(EVENT_NAME))
  }, [role])

  const canSwitch = role === 'provider'

  return { activeMode, setActiveMode, canSwitch }
}
